/* ========================================================================
   new-arrivals 専用の仮想コレクション描画制御
   ======================================================================== */
(() => {
  const AuremePlpUtils = window.AuremePlpUtils;
  const AuremePlpNewArrival = window.AuremePlpNewArrival;
  if (!AuremePlpUtils || !AuremePlpNewArrival) return;

  const {
    ROOT_SELECTOR,
    buildSearchParamsString,
    buildSearchUrl,
    clearQueuedFocusAfterRender,
    createSearchParams,
    focusQueuedElementAfterRender,
    getRootForElement,
    isElementTarget,
    parsePositiveInteger,
    queueFocusAfterRender,
    requestFacetRender,
  } = AuremePlpUtils;

  const {
    buildNewArrivalCountBaseSearchParams,
    buildNewArrivalDatasetUrl,
    buildNewArrivalPageSearchParams,
    clearNewArrivalQueryCache,
    facetItemMatchesNewArrivalCriteria,
    getNewArrivalFacetGroups,
    getNewArrivalQueryData,
    getNewArrivalVirtualConfig,
    normalizeNewArrivalSearchParams,
    renderNewArrivalPagination,
    syncNewArrivalVirtualFacetAvailability,
  } = AuremePlpNewArrival;

  let newArrivalRenderToken = 0;

  function setNewArrivalPendingState(isPending) {
    const collectionContainer = document
      .getElementById('ProductGridContainer')
      ?.querySelector('.collection');
    if (!collectionContainer) return;

    collectionContainer.classList.toggle('loading', isPending);
  }

  function buildNewArrivalCountText(config, productCount, allProductCount) {
    if (productCount === allProductCount) {
      return config.productCountSimpleTemplate.replaceAll('__COUNT__', String(productCount));
    }

    return config.productCountTemplate
      .replaceAll('__PRODUCT_COUNT__', String(productCount))
      .replaceAll('__COUNT__', String(allProductCount));
  }

  function renderNewArrivalCount(config, productCount, allProductCount) {
    const countText = buildNewArrivalCountText(config, productCount, allProductCount);

    for (const el of [
      document.getElementById('ProductCount'),
      document.getElementById('ProductCountDesktop'),
    ]) {
      if (!el) continue;
      el.textContent = countText;
      el.classList.remove('loading');
    }
  }

  function updateNewArrivalDocumentTitle(currentPage) {
    const currentTitle = document.title || '';
    if (!currentTitle) return;

    const baseTitle = currentTitle.replace(/ – Page \d+(?= – )/, '');
    if (currentPage <= 1) {
      document.title = baseTitle;
      return;
    }

    const titleParts = baseTitle.split(' – ');
    if (titleParts.length < 2) {
      document.title = `${baseTitle} – Page ${currentPage}`;
      return;
    }

    const titleSuffix = titleParts.pop();
    document.title = `${titleParts.join(' – ')} – Page ${currentPage} – ${titleSuffix}`;
  }

  function renderNewArrivalProductGrid(itemMarkupList, currentPage, productsPerPage) {
    const productGrid = document.getElementById('product-grid');
    const emptyStateElement = document.querySelector('[data-aureme-plp-empty-state]');
    if (!productGrid) return;

    const offset = (currentPage - 1) * productsPerPage;
    const pagedMarkup = itemMarkupList.slice(offset, offset + productsPerPage).join('');

    productGrid.innerHTML = pagedMarkup; // サーバー描画済みのShopifyセクションHTMLを挿入
    productGrid.hidden = itemMarkupList.length === 0;

    if (emptyStateElement) {
      emptyStateElement.hidden = itemMarkupList.length !== 0;
    }
  }

  function replaceHistoryStateSearchParams(searchParams) {
    const query = buildSearchParamsString(searchParams);
    const currentState = history.state && typeof history.state === 'object' ? history.state : {};

    history.replaceState(
      {
        ...currentState,
        searchParams: query,
      },
      '',
      buildSearchUrl(searchParams),
    );
  }

  async function renderNewArrivalVirtualCollection(
    root = document.querySelector(ROOT_SELECTOR),
    searchParams = window.location.search,
  ) {
    const config = getNewArrivalVirtualConfig(root);
    if (!config) {
      focusQueuedElementAfterRender();
      return;
    }

    /* facet / pagination 操作が連続した場合は、最後に開始した描画だけ DOM 反映と focus 復帰を行う。 */
    const renderToken = ++newArrivalRenderToken;
    setNewArrivalPendingState(true);

    try {
      const currentSearchParams = createSearchParams(searchParams);
      const datasetSearchParams = buildNewArrivalCountBaseSearchParams(root, currentSearchParams);
      const groups = getNewArrivalFacetGroups(root);
      const requestedPage = parsePositiveInteger(currentSearchParams.get('page'), 1);
      const currentQueryData = await getNewArrivalQueryData(
        root,
        datasetSearchParams,
        currentSearchParams,
      );
      if (renderToken !== newArrivalRenderToken) return;

      const filteredEntries = currentQueryData.entries.filter((entry) =>
        facetItemMatchesNewArrivalCriteria(entry.facetItem, groups, '', null),
      );
      const filteredItems = filteredEntries.map((entry) => entry.html);
      const allProductCount = currentQueryData.totalCount;

      const totalPages =
        filteredItems.length > 0 ? Math.ceil(filteredItems.length / config.productsPerPage) : 1;
      const currentPage = filteredItems.length > 0 ? Math.min(requestedPage, totalPages) : 1;

      /*
       * facet 絞り込み後に存在しない page が残った場合は、描画結果と URL を同じ page に丸める。
       * page=1 は canonical な 1 ページ目 URL に戻し、戻る/進む時の仮想ページ状態をずらさない。
       */
      if (requestedPage !== currentPage || (currentPage === 1 && currentSearchParams.has('page'))) {
        replaceHistoryStateSearchParams(
          buildNewArrivalPageSearchParams(currentSearchParams, currentPage),
        );
      }

      renderNewArrivalProductGrid(filteredItems, currentPage, config.productsPerPage);
      renderNewArrivalCount(config, filteredItems.length, allProductCount);
      renderNewArrivalPagination(config, currentSearchParams, currentPage, totalPages);
      updateNewArrivalDocumentTitle(currentPage);
      syncNewArrivalVirtualFacetAvailability(
        root,
        groups,
        currentQueryData.facetItems,
        () => renderToken === newArrivalRenderToken,
      );
    } catch (error) {
      console.error(error);
    } finally {
      if (renderToken === newArrivalRenderToken) {
        setNewArrivalPendingState(false);
        focusQueuedElementAfterRender();
      }
    }
  }

  function handleNewArrivalPaginationClick(target, event) {
    const newArrivalPaginationLink = target.closest('[data-aureme-new-arrival-pagination-link]');
    if (!newArrivalPaginationLink) return false;

    /* 修飾キー付きクリックや別タブ遷移はブラウザ既定動作を維持する。 */
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return true;
    }

    event.preventDefault();

    const root = getRootForElement(newArrivalPaginationLink);
    const nextPage = parsePositiveInteger(newArrivalPaginationLink.dataset.page, 1);
    const searchParams = buildNewArrivalPageSearchParams(window.location.search, nextPage);

    if (root?.dataset.sectionId) {
      queueFocusAfterRender(
        `#AuremePlpPagination-${root.dataset.sectionId} .pagination__item--current`,
      );
    }

    requestFacetRender(searchParams, root);
    return true;
  }

  function initializeNewArrivalPlp() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!getNewArrivalVirtualConfig(root)) return;

    void renderNewArrivalVirtualCollection(root);
  }

  document.addEventListener('click', (event) => {
    if (!isElementTarget(event.target)) return;
    handleNewArrivalPaginationClick(event.target, event);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeNewArrivalPlp);
  } else {
    initializeNewArrivalPlp();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      /* カート更新後は Quick Add が表示内容を差し替えるため、古いカード情報を持つ一覧キャッシュは破棄する */
      clearNewArrivalQueryCache();
    });
  }

  window.AuremePlp = Object.assign(window.AuremePlp || {}, {
    buildNewArrivalDatasetUrl,
    clearQueuedFocusAfterRender,
    normalizeSearchParams(searchParams, root = document.querySelector(ROOT_SELECTOR)) {
      const { searchParams: normalizedSearchParams, didChange } = normalizeNewArrivalSearchParams(
        searchParams,
        root,
      );

      return {
        searchParams: normalizedSearchParams.toString(),
        didChange,
      };
    },
  });

  document.addEventListener('aureme:plp:rendered', (event) => {
    void renderNewArrivalVirtualCollection(
      document.querySelector(ROOT_SELECTOR),
      event.detail?.searchParams,
    );
  });
})();
